export const DEFAULT_AGENT_ALERTS_OPTION_ID = "__default__";

const POD_AGENT_ALERT_DISPLAY_SECTIONS = [
  { key: "alerts", label: "Alerts" },
  { key: "instructor", label: "Instructor" },
  { key: "courseName", label: "Course Name" },
  { key: "examName", label: "Exam Name" },
  {
    key: "schedule",
    label: "Start Date - End Date - Duration (minutes)",
    combineKeys: ["startDate", "endDate", "duration"],
  },
  { key: "numOfStudents", label: "Number of Students" },
  { key: "timezone", label: "Timezone" },
  { key: "accessInstructions", label: "Exam Access Instructions" },
  { key: "examCodes", label: "Exam Codes/Passwords" },
  { key: "lockdownBrowser", label: "Lockdown Browser Instructions" },
  { key: "scratchPaperInstructions", label: "Scratch Paper Instructions" },
  { key: "calculatorInstructions", label: "Calculator Instructions" },
  { key: "additionalMaterials", label: "Additional Materials" },
  { key: "accommodations", label: "Accommodations" },
  { key: "additionalInstructions", label: "Additional Instructions" },
];

function hasValue(value) {
  return value != null && String(value).trim() !== "";
}

function trimValue(value) {
  return hasValue(value) ? String(value).trim() : null;
}

export function formatAgentAlertFieldText(value) {
  if (value == null) return "";

  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\n/g, "\n");
}

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_URL_PUNCTUATION_REGEX = /[.,;:!?)}'"”’\]]+$/;

export function splitTextWithUrls(text) {
  const input = String(text ?? "");
  const segments = [];
  let lastIndex = 0;

  for (const match of input.matchAll(URL_REGEX)) {
    const fullMatch = match[0];
    const index = match.index ?? 0;
    const trailingPunctuation =
      fullMatch.match(TRAILING_URL_PUNCTUATION_REGEX)?.[0] ?? "";
    const url = trailingPunctuation
      ? fullMatch.slice(0, -trailingPunctuation.length)
      : fullMatch;

    if (!url) continue;

    if (index > lastIndex) {
      segments.push({ type: "text", value: input.slice(lastIndex, index) });
    }

    segments.push({ type: "url", value: url });
    lastIndex = index + url.length;
  }

  if (lastIndex < input.length) {
    segments.push({ type: "text", value: input.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", value: input }];
}

export function formatAgentAlertSummary({ instructor, courseName, examName }) {
  const parts = [instructor, courseName, examName]
    .map(trimValue)
    .filter(Boolean);

  return parts.join(" - ") || "Untitled exam";
}

function formatCombinedFieldValue(alert, keys) {
  const parts = keys.map((key) => trimValue(alert[key])).filter(Boolean);
  return parts.length > 0 ? parts.join(" - ") : null;
}

export function getVisiblePodAgentAlertFields(alert) {
  if (!alert) return [];

  return POD_AGENT_ALERT_DISPLAY_SECTIONS.map(({ key, label, combineKeys }) => {
    const value = combineKeys
      ? formatCombinedFieldValue(alert, combineKeys)
      : trimValue(alert[key]);

    return { key, label, value };
  }).filter(({ value }) => value);
}

export function getAgentAlertOptionFields(alert) {
  return {
    instructor: trimValue(alert?.instructor),
    courseName: trimValue(alert?.courseName),
    examName: trimValue(alert?.examName),
    startDate: trimValue(alert?.startDate),
    endDate: trimValue(alert?.endDate),
  };
}

export function buildAgentAlertsOptions(organization, podAgentAlerts = []) {
  const options = [
    {
      id: DEFAULT_AGENT_ALERTS_OPTION_ID,
      label: "Default",
      isDefault: true,
      instructor: null,
      courseName: null,
      examName: null,
      startDate: null,
      endDate: null,
      source: "organization",
      data: organization,
    },
  ];

  for (const alert of podAgentAlerts) {
    if (alert?.isHidden) continue;

    const fields = getAgentAlertOptionFields(alert);

    options.push({
      id: alert.id,
      label: formatAgentAlertSummary(fields),
      isDefault: false,
      ...fields,
      source: "podAgentAlert",
      data: alert,
    });
  }

  return options;
}

export function findUniqueAgentAlertOptionIdByExamLabel(options, examValue) {
  const normalizedExam = trimValue(examValue);
  if (!normalizedExam) return null;

  const matchingOptions = options.filter(
    (option) => !option.isDefault && option.label === normalizedExam,
  );

  return matchingOptions.length === 1 ? matchingOptions[0].id : null;
}
